import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import { LogoMark, LogoWordmark } from "@m2s/design-system";

import { api, extractErrorMessage, setToken } from "@/lib/api";
import type { LoginResponse, MfaAddDeviceResponse } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

type FormValues = { email: string; password: string };

export function LoginPage() {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const currentTenantSlug = useAuthStore((s) => s.tenant?.slug);
  const clearSession = useAuthStore((s) => s.clear);

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") ?? "/";
  const justActivated = params.get("activated") === "1";
  const justReset = params.get("reset") === "1";
  // Sign in to a specific workspace (tenant slug), e.g. from a "you're the
  // admin of <workspace>" email. Omitted → the account's primary workspace.
  const workspace = params.get("workspace")?.trim().toLowerCase() || null;

  const isDev = import.meta.env.DEV;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: isDev
      ? { email: "priya@lighthouse.local", password: "DevPassword!1" }
      : { email: "", password: "" },
  });
  const [error, setError] = useState<string | null>(null);

  // Two-factor step. Once /auth/login returns mfa_required we hold the
  // short-lived pending token IN MEMORY ONLY (never localStorage) and swap the
  // password form for the OTP form. Exchanging it at /auth/mfa/verify finishes
  // the sign-in.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  // True when this is a forced first-time enrolment (scan a QR) vs just entering
  // a code for an already-registered authenticator.
  const [mfaSetup, setMfaSetup] = useState(false);
  // True when the account can register an additional authenticator at login
  // (admin accounts under their device cap). Drives the "Add this device" button.
  const [canAddDevice, setCanAddDevice] = useState(false);
  // True when the user clicked "Add this device" on the OTP card — switches to
  // the QR-scan flow that targets /auth/mfa/add-device/*.
  const [addingDevice, setAddingDevice] = useState(false);
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  // Already signed in → straight in, unless the link asks for a different
  // workspace; then offer to sign out and continue (see below).
  const switchingWorkspace = Boolean(user && workspace && workspace !== currentTenantSlug);
  if (user && !switchingWorkspace) return <Navigate to={next} replace />;

  // Full reload after sign-out (same as the topbar) so nothing cached from the
  // current workspace carries over into the next one.
  const signOutAndContinue = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    clearSession();
    window.location.assign(`/login?workspace=${encodeURIComponent(workspace ?? "")}`);
  };

  const completeSession = (data: LoginResponse) => {
    if (!data.access_token || !data.user || !data.tenant) return;
    setToken(data.access_token);
    setSession({
      user: data.user,
      tenant: data.tenant,
      memberships: data.memberships,
      profileComplete: data.profile_complete,
    });
    navigate(next, { replace: true });
  };

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      const { data } = await api.post<LoginResponse>(
        "/auth/login",
        workspace ? { ...values, workspace } : values,
      );
      if (data.mfa_required && data.mfa_pending_token) {
        setMfaSetup(Boolean(data.mfa_setup_required));
        setCanAddDevice(Boolean(data.mfa_can_add_device));
        setMfaToken(data.mfa_pending_token);
        return;
      }
      completeSession(data);
    } catch (err) {
      const message =
        (err as { response?: { data?: { detail?: string } } } | undefined)?.response?.data
          ?.detail ?? "Couldn't sign in. Check your email and password.";
      setError(message);
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setMfaError(null);
    setMfaSubmitting(true);
    try {
      const { data } = await api.post<LoginResponse>("/auth/mfa/verify", {
        pending_token: mfaToken,
        code: otp.trim(),
      });
      completeSession(data);
    } catch (err) {
      const message =
        (err as { response?: { data?: { detail?: string } } } | undefined)?.response?.data
          ?.detail ?? "That code didn't match. Try again.";
      setMfaError(message);
    } finally {
      setMfaSubmitting(false);
    }
  };

  const resetMfa = () => {
    setMfaToken(null);
    setMfaSetup(false);
    setCanAddDevice(false);
    setAddingDevice(false);
    setOtp("");
    setMfaError(null);
    setUseRecovery(false);
  };

  // Forced first-time enrolment — scan a QR, confirm a code.
  if (mfaToken && mfaSetup) {
    return (
      <EnrollCard
        mode="first"
        pendingToken={mfaToken}
        onComplete={completeSession}
        onBack={resetMfa}
      />
    );
  }

  // Self-serve add-device path (admin chose "Add this device" on the OTP card).
  if (mfaToken && addingDevice) {
    return (
      <EnrollCard
        mode="additional"
        pendingToken={mfaToken}
        onComplete={completeSession}
        onBack={() => setAddingDevice(false)}
      />
    );
  }

  // Already enrolled — just enter a code.
  if (mfaToken) {
    return (
      <OtpCard
        otp={otp}
        setOtp={setOtp}
        useRecovery={useRecovery}
        setUseRecovery={setUseRecovery}
        error={mfaError}
        submitting={mfaSubmitting}
        onVerify={onVerify}
        onBack={resetMfa}
        canAddDevice={canAddDevice}
        onAddDevice={() => {
          setMfaError(null);
          setOtp("");
          setAddingDevice(true);
        }}
      />
    );
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--canvas)",
        padding: 32,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 32,
          background: "var(--canvas)",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
        }}
      >
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={56} withDial />
          </div>
          <LogoWordmark size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.2px", margin: "16px 0 0", color: "var(--body)" }}>
            Sign in
          </h1>
        </div>

        {workspace && (
          <div role="status" style={workspaceNote}>
            {switchingWorkspace ? (
              <>
                You're signed in as <strong>{user?.email}</strong>. Sign out to open the{" "}
                <strong>{workspace}</strong> workspace.
              </>
            ) : (
              <>
                Signing in to the <strong>{workspace}</strong> workspace.
              </>
            )}
          </div>
        )}

        {justActivated && (
          <div
            role="status"
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              fontSize: 13,
              color: "#047a18",
              background: "rgba(0,215,34,0.08)",
              border: "1px solid rgba(0,215,34,0.3)",
              borderRadius: 4,
            }}
          >
            Account activated. Sign in with your new password.
          </div>
        )}

        {justReset && (
          <div
            role="status"
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              fontSize: 13,
              color: "#047a18",
              background: "rgba(0,215,34,0.08)",
              border: "1px solid rgba(0,215,34,0.3)",
              borderRadius: 4,
            }}
          >
            Password updated. Sign in with your new password.
          </div>
        )}

        {switchingWorkspace ? (
          <button type="button" onClick={signOutAndContinue} style={switchBtn}>
            Sign out and continue
          </button>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Field label="Email" error={errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              {...register("email", { required: "Email is required" })}
              style={inputStyle}
            />
          </Field>

          <Field label="Password" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="current-password"
              {...register("password", { required: "Password is required" })}
              style={inputStyle}
            />
          </Field>

          {error && (
            <p
              role="alert"
              style={{
                marginTop: 12,
                marginBottom: 12,
                padding: "8px 12px",
                fontSize: 13,
                color: "#ee1d36",
                background: "rgba(238,29,54,0.06)",
                border: "1px solid rgba(238,29,54,0.3)",
                borderRadius: 4,
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: "100%",
              marginTop: 16,
              background: "var(--ink)",
              color: "var(--on-primary)",
              border: "none",
              borderRadius: 4,
              padding: "11px 16px",
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 14,
              cursor: isSubmitting ? "wait" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>

          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Link
              to="/forgot"
              style={{
                fontSize: 13,
                color: "var(--mute)",
                textDecoration: "none",
              }}
            >
              Forgot password?
            </Link>
          </div>
        </form>
        )}

        {isDev && (
          <p
            style={{
              marginTop: 18,
              fontSize: 12,
              color: "var(--mute)",
              fontFamily: "var(--font-mono)",
              lineHeight: 1.5,
            }}
          >
            dev seed · priya@lighthouse.local / DevPassword!1
          </p>
        )}
      </div>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "10px 12px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const workspaceNote: React.CSSProperties = {
  marginBottom: 16,
  padding: "10px 14px",
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--body)",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
};

const switchBtn: React.CSSProperties = {
  width: "100%",
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "11px 16px",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: 14,
  cursor: "pointer",
};

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {error && (
        <span style={{ display: "block", color: "#ee1d36", fontSize: 12, marginTop: 4 }}>
          {error}
        </span>
      )}
    </label>
  );
}

const linkBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 13,
  color: "var(--mute)",
  cursor: "pointer",
  padding: 0,
};

/** The second login step — enter a TOTP code (or a recovery code). */
function OtpCard({
  otp,
  setOtp,
  useRecovery,
  setUseRecovery,
  error,
  submitting,
  onVerify,
  onBack,
  canAddDevice,
  onAddDevice,
}: {
  otp: string;
  setOtp: (v: string) => void;
  useRecovery: boolean;
  setUseRecovery: (v: boolean) => void;
  error: string | null;
  submitting: boolean;
  onVerify: (e: React.FormEvent) => void;
  onBack: () => void;
  canAddDevice: boolean;
  onAddDevice: () => void;
}) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--canvas)",
        padding: 32,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 32,
          background: "var(--canvas)",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
        }}
      >
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={56} withDial />
          </div>
          <LogoWordmark size={22} />
          <h1
            style={{
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "-0.2px",
              margin: "16px 0 0",
              color: "var(--body)",
            }}
          >
            Two-step verification
          </h1>
          <p style={{ fontSize: 13, color: "var(--mute)", marginTop: 8, lineHeight: 1.5 }}>
            {useRecovery
              ? "Enter one of your recovery codes."
              : "Enter the 6-digit code from your authenticator app."}
          </p>
        </div>

        <form onSubmit={onVerify}>
          <Field label={useRecovery ? "Recovery code" : "Authentication code"}>
            <input
              autoFocus
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder={useRecovery ? "xxxxxx-xxxxxx" : "123456"}
              style={{
                ...inputStyle,
                textAlign: "center",
                letterSpacing: useRecovery ? "normal" : "0.3em",
                fontFamily: "var(--font-mono)",
              }}
            />
          </Field>

          {error && (
            <p
              role="alert"
              style={{
                marginTop: 12,
                marginBottom: 12,
                padding: "8px 12px",
                fontSize: 13,
                color: "#ee1d36",
                background: "rgba(238,29,54,0.06)",
                border: "1px solid rgba(238,29,54,0.3)",
                borderRadius: 4,
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !otp.trim()}
            style={{
              width: "100%",
              marginTop: 4,
              background: "var(--ink)",
              color: "var(--on-primary)",
              border: "none",
              borderRadius: 4,
              padding: "11px 16px",
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 14,
              cursor: submitting || !otp.trim() ? "not-allowed" : "pointer",
              opacity: submitting || !otp.trim() ? 0.6 : 1,
            }}
          >
            {submitting ? "Verifying…" : "Verify"}
          </button>
        </form>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={() => {
              setUseRecovery(!useRecovery);
              setOtp("");
            }}
            style={linkBtnStyle}
          >
            {useRecovery ? "Use your authenticator app instead" : "Use a recovery code"}
          </button>
          {canAddDevice && (
            <button type="button" onClick={onAddDevice} style={linkBtnStyle}>
              First time on this device? Add it now
            </button>
          )}
          <button type="button" onClick={onBack} style={linkBtnStyle}>
            Back to sign in
          </button>
        </div>
      </div>
    </main>
  );
}

/** The card shell (logo + title) shared by the MFA login steps. */
function MfaShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--canvas)",
        padding: 32,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 32,
          background: "var(--canvas)",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
        }}
      >
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <LogoMark size={56} withDial />
          </div>
          <LogoWordmark size={22} />
          <h1
            style={{
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: "-0.2px",
              margin: "16px 0 0",
              color: "var(--body)",
            }}
          >
            {title}
          </h1>
        </div>
        {children}
      </div>
    </main>
  );
}

/** Forced first-time enrolment at login (mode="first") or a second admin
 * teammate self-registering a NEW device under the shared account's cap
 * (mode="additional"). Same UI shell — different endpoints + copy + no
 * recovery-codes step on the additional path. */
function EnrollCard({
  mode,
  pendingToken,
  onComplete,
  onBack,
}: {
  mode: "first" | "additional";
  pendingToken: string;
  onComplete: (data: LoginResponse) => void;
  onBack: () => void;
}) {
  const [setup, setSetup] = useState<MfaAddDeviceResponse | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [session, setSession] = useState<LoginResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);

  const startPath =
    mode === "first" ? "/auth/mfa/enroll/start" : "/auth/mfa/add-device/start";
  const confirmPath =
    mode === "first" ? "/auth/mfa/enroll/confirm" : "/auth/mfa/add-device/confirm";

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .post<MfaAddDeviceResponse>(startPath, { pending_token: pendingToken })
      .then((r) => setSetup(r.data))
      .catch((err) =>
        setError(extractErrorMessage(err, "Could not start setup. Sign in again.")),
      );
  }, [pendingToken, startPath]);

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<LoginResponse>(confirmPath, {
        pending_token: pendingToken,
        code: code.trim(),
      });
      if (data.recovery_codes && data.recovery_codes.length > 0) {
        setRecovery(data.recovery_codes);
        setSession(data);
      } else {
        onComplete(data);
      }
    } catch (err) {
      setError(extractErrorMessage(err, "That code didn't match. Try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (recovery && session) {
    return (
      <MfaShell title="Save your recovery codes">
        <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 12px", lineHeight: 1.5 }}>
          Store these somewhere safe. Each one signs you in once if you lose your phone. They
          won't be shown again.
        </p>
        <div style={recoveryGrid}>
          {recovery.map((c) => (
            <code key={c} style={recoveryCode}>
              {c}
            </code>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(recovery.join("\n"))?.catch(() => {});
            onComplete(session);
          }}
          style={fullPrimaryBtn}
        >
          Copy and continue
        </button>
      </MfaShell>
    );
  }

  const title = mode === "first" ? "Set up two-factor" : "Add this device";
  const intro =
    mode === "first"
      ? "Your account requires an authenticator app. Scan this with Google Authenticator (or Microsoft Authenticator / Authy), then enter the 6-digit code it shows."
      : "Scan this QR with your authenticator app (Google Authenticator / Microsoft Authenticator / Authy), then enter the 6-digit code to register this device on the account.";

  return (
    <MfaShell title={title}>
      <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 16px", lineHeight: 1.5 }}>
        {intro}
      </p>

      {error && (
        <p role="alert" style={mfaAlert}>
          {error}
        </p>
      )}

      {setup ? (
        <>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <div
              style={{
                background: "#fff",
                padding: 10,
                border: "1px solid var(--hairline)",
                borderRadius: 6,
              }}
            >
              <QRCodeSVG value={setup.otpauth_uri} size={160} />
            </div>
          </div>
          <p style={{ fontSize: 11, color: "var(--mute)", textAlign: "center", marginBottom: 14 }}>
            Can't scan? Enter this key:
            <br />
            <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, wordBreak: "break-all" }}>
              {setup.secret}
            </code>
          </p>
          <form onSubmit={onConfirm}>
            <input
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                ...inputStyle,
                textAlign: "center",
                letterSpacing: "0.3em",
                fontFamily: "var(--font-mono)",
              }}
            />
            <button
              type="submit"
              disabled={submitting || !code.trim()}
              style={{
                ...fullPrimaryBtn,
                marginTop: 12,
                cursor: submitting || !code.trim() ? "not-allowed" : "pointer",
                opacity: submitting || !code.trim() ? 0.6 : 1,
              }}
            >
              {submitting ? "Verifying…" : "Verify and continue"}
            </button>
          </form>
        </>
      ) : (
        !error && <p style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</p>
      )}

      <div style={{ textAlign: "center", marginTop: 14 }}>
        <button type="button" onClick={onBack} style={linkBtnStyle}>
          Back to sign in
        </button>
      </div>
    </MfaShell>
  );
}

const fullPrimaryBtn: React.CSSProperties = {
  width: "100%",
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "11px 16px",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: 14,
  cursor: "pointer",
};

const mfaAlert: React.CSSProperties = {
  marginBottom: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};

const recoveryGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 8,
  padding: 14,
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  marginBottom: 14,
};

const recoveryCode: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  color: "var(--ink)",
};
